import React, { useState, useEffect, useRef } from "react";
import {
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaBriefcase,
  FaGraduationCap,
  FaUniversity,
  FaEnvelope,
  FaGlobe,
  FaTools,
  FaStar,
  FaTextHeight,
  FaUpload,
  FaTimes,
  FaLock,
  FaFile,
  FaEye,
  FaEyeSlash,
  FaMoon,
  FaSun
} from "react-icons/fa";
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SummaryApi from "../common/index";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MultiSelectDropdown from "../pages/MultiSelectDropdown";

const CreateInstructorForm = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    alternate_mobile_number: "",
    address: "",
    job_title: "",
    total_experience: "",
    qualification: "",
    study_university: "",
    email: "",
    website: "",
    skills: "",
    rating: "",
    description: "",
    about_instructor1: "",
    about_instructor2: "",
    instructor_image: null,
    document1: null,
    document2: null,
    document3: null,
    document4: null,
    document5: null,
    password: "",
    is_active: 0,
    instructor_id: "",
    selectedCategories: [],
    selectedCourses: [],
    is_self_registered: 0,
  });

  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [documentPreviews, setDocumentPreviews] = useState({
    document1: null,
    document2: null,
    document3: null,
    document4: null,
    document5: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [hasError, setHasError] = useState({
    first_name: false,
    last_name: false,
    mobile_number: false,
    email: false,
    selectedCategories: false,
    selectedCourses: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [activeCategories, setActiveCategories] = useState([]);
  const [allActiveCourses, setAllActiveCourses] = useState([]);
  const [availableCoursesForSelection, setAvailableCoursesForSelection] = useState([]);
const [associatedCourseIds, setAssociatedCourseIds] = useState([]);
  const [associatedCategoriesDisplay, setAssociatedCategoriesDisplay] = useState([]);
  const [associatedCoursesDisplay, setAssociatedCoursesDisplay] = useState([]);

  const fetchedInstructorDataRef = useRef(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    sessionStorage.setItem('admin-theme', newDarkMode ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', newDarkMode);
    
    // Dispatch event for other components
    window.dispatchEvent(new Event('themeChanged'));
    window.dispatchEvent(new Event('adminThemeToggled'));
  };

  // Helper function to format image URLs
  const formatImageUrl = (imagePath) => {
    if (!imagePath) return "";

    if (imagePath.startsWith("http")) {
      return imagePath;
    }

    const baseUrl = SummaryApi.Images.url.replace("/api", "");

    if (imagePath.includes(baseUrl)) {
      return imagePath;
    }

    if (imagePath.startsWith("uploads/")) {
      return `${baseUrl}/${imagePath}`;
    }

    return `${baseUrl}/${imagePath}`;
  };

  const isImage = (path) => {
    if (!path) return false;
    const ext =
      typeof path === "string"
        ? path.split(".").pop().toLowerCase()
        : path.name?.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext);
  };

  // 1. Fetch Categories and Courses on Mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const categoryResponse = await fetch(
          SummaryApi.getActiveCategories.url,
          {
            method: SummaryApi.getActiveCategories.method,
          }
        );
        const categoryData = await categoryResponse.json();
        if (categoryData.status) {
          setActiveCategories(categoryData.data);
        } else {
          toast.error(categoryData.message || "Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchInitialData();
  }, []);
// 2. Fetch Courses by Selected Categories
useEffect(() => {
  const fetchCoursesByCategory = async () => {
    if (formData.selectedCategories.length > 0) {
      try {
        const courseResponse = await fetch(
          SummaryApi.getActiveCoursesBycategory.url,
          {
            method: SummaryApi.getActiveCoursesBycategory.method,
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              category_ids: formData.selectedCategories,
            }),
          }
        );
        const courseData = await courseResponse.json();
        if (courseData.status) {
          setAllActiveCourses(courseData.data);
          
          // Filter out already associated courses in edit mode
          let filteredCourses = courseData.data;
          if (isEdit && associatedCourseIds.length > 0) {
            filteredCourses = courseData.data.filter(
              course => !associatedCourseIds.includes(String(course.course_id))
            );
          }
          
          setAvailableCoursesForSelection(filteredCourses);
          
          const availableCourseIds = filteredCourses.map(course => String(course.course_id));
          const filteredSelectedCourses = formData.selectedCourses.filter(
            courseId => availableCourseIds.includes(courseId)
          );
          
          if (filteredSelectedCourses.length !== formData.selectedCourses.length) {
            setFormData(prev => ({
              ...prev,
              selectedCourses: filteredSelectedCourses
            }));
          }
        } else {
          toast.error(courseData.message || "Failed to fetch courses");
          setAllActiveCourses([]);
          setAvailableCoursesForSelection([]);
          setFormData(prev => ({ ...prev, selectedCourses: [] }));
        }
      } catch (error) {
        console.error("Error fetching courses by category:", error);
        toast.error("Error fetching courses");
        setAllActiveCourses([]);
        setAvailableCoursesForSelection([]);
        setFormData(prev => ({ ...prev, selectedCourses: [] }));
      }
    } else {
      setAllActiveCourses([]);
      setAvailableCoursesForSelection([]);
      if (formData.selectedCourses.length > 0) {
        setFormData(prev => ({ ...prev, selectedCourses: [] }));
      }
    }
  };

  fetchCoursesByCategory();
}, [formData.selectedCategories, isEdit, associatedCourseIds]);
  // 3. Handle URL parameters and fetch instructor details for edit mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedId = urlParams.get("id");

    if (encodedId) {
      try {
        const decodedId = atob(encodedId);
        setIsEdit(true);
        setFormData((prev) => ({ ...prev, instructor_id: decodedId }));
        fetchInstructor(decodedId);
      } catch (error) {
        console.error("Invalid encoded ID:", error);
        toast.error("Invalid instructor ID");
      }
    }
  }, []);

  const fetchInstructor = async (id) => {
    try {
      const response = await fetch(SummaryApi.InstructorByid.url, {
        method: SummaryApi.InstructorByid.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ instructor_id: id }),
      });
      const data = await response.json();

      if (data.status) {
        fetchedInstructorDataRef.current = data.data;
      } else {
        toast.error(data.message || "Failed to fetch instructor");
        fetchedInstructorDataRef.current = null;
      }
    } catch (error) {
      console.error("Error fetching instructor:", error);
      toast.error("Error fetching instructor");
      fetchedInstructorDataRef.current = null;
    }
  };

  // 4. Coordinate setting formData *after* all lists and instructor data are available
  useEffect(() => {
    if (
      fetchedInstructorDataRef.current &&
      activeCategories.length > 0 &&
      (allActiveCourses.length > 0 || formData.selectedCategories.length === 0)
    ) {
      const instructor = fetchedInstructorDataRef.current;

      const instructorCategories = instructor.category_id
        ? Array.isArray(instructor.category_id)
          ? instructor.category_id.map(String)
          : [String(instructor.category_id)]
        : [];
      const instructorCourses = instructor.course_id
        ? Array.isArray(instructor.course_id)
          ? instructor.course_id.map(String)
          : [String(instructor.course_id)]
        : [];

      setFormData((prev) => ({
        ...prev,
        ...instructor,
        instructor_id: prev.instructor_id || instructor.instructor_id,
        selectedCategories: instructorCategories,
        selectedCourses: instructorCourses,
        is_active: instructor.is_active === 1 ? 1 : 0,
      }));

      if (instructor.instructor_image) {
        setImagePreview(formatImageUrl(instructor.instructor_image));
      }
      [
        "document1",
        "document2",
        "document3",
        "document4",
        "document5",
      ].forEach((doc) => {
        if (instructor[doc]) {
          setDocumentPreviews((prev) => ({
            ...prev,
            [doc]: formatImageUrl(instructor[doc]),
          }));
        }
      });

      fetchedInstructorDataRef.current = null;
    }
  }, [activeCategories, allActiveCourses, fetchedInstructorDataRef.current]);

  // 5. Fetch associated courses/categories for display (when in edit mode)
// 5. Fetch associated courses/categories for display (when in edit mode)
useEffect(() => {
  const fetchAssociatedCourseData = async () => {
    if (isEdit && formData.instructor_id) {
      try {
        const response = await fetch(
          SummaryApi.getCourseIdByInstructorId.url,
          {
            method: SummaryApi.getCourseIdByInstructorId.method,
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ instructor_id: formData.instructor_id }),
          }
        );
        const data = await response.json();

        if (data.status) {
          const uniqueCategories = [
            ...new Set(data.data.map((item) => item.category_name)),
          ];
          const uniqueCourses = [
            ...new Set(data.data.map((item) => item.course_title)),
          ];
          
          // Store the associated course IDs
          const associatedIds = data.data.map(item => String(item.course_id));
          setAssociatedCourseIds(associatedIds);
          
          setAssociatedCategoriesDisplay(uniqueCategories);
          setAssociatedCoursesDisplay(uniqueCourses);
        } else {
          setAssociatedCourseIds([]);
          setAssociatedCategoriesDisplay([]);
          setAssociatedCoursesDisplay([]);
        }
      } catch (error) {
        console.error("Error fetching associated courses for display:", error);
        setAssociatedCourseIds([]);
        setAssociatedCategoriesDisplay([]);
        setAssociatedCoursesDisplay([]);
      }
    }
  };

  fetchAssociatedCourseData();
}, [isEdit, formData.instructor_id]);
  const handleCreateTimeslot = async (instructorId, courseId) => {
    const idInstructor = String(instructorId);
    const idCourse = String(courseId);

    if (!idInstructor || !idCourse) {
      console.error(
        "handleCreateTimeslot: Missing required IDs for timeslot. Instructor ID:",
        instructorId,
        "Course ID:",
        courseId
      );
      return {
        status: false,
        message: "Missing required Instructor or Course ID for timeslot.",
        origin: "validation",
      };
    }

    try {
      const response = await fetch(SummaryApi.createInstructorCourse.url, {
        method: SummaryApi.createInstructorCourse.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          instructor_id: idInstructor,
          course_id: idCourse,
        }),
      });
      const data = await response.json();

      if (data.status) {
        return {
          status: true,
          message: `Timeslot created for course ID ${idCourse}!`,
          origin: "api_success",
        };
      } else {
        const errorMessage =
          data.message ||
          `Failed to create timeslot for Course ID ${courseId} (API reported error).`;
        console.error(
          `handleCreateTimeslot: API error for Course ID ${courseId}:`,
          data
        );
        toast.error(errorMessage);
        return {
          status: false,
          message: errorMessage,
          apiResponse: data,
          origin: "api_fail", 
        };
      }
    } catch (error) {
      console.error(
        `handleCreateTimeslot: Network or unexpected error for Course ID ${courseId}:`,
        error
      );
      const errorMessage = `Network or unexpected error creating timeslot for Course ID ${courseId}.`;
      toast.error(errorMessage);
      return {
        status: false,
        message: errorMessage,
        error: error,
        origin: "network_error",
      };
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "mobile_number" || name === "alternate_mobile_number") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else if (name === "is_active") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (["first_name", "last_name", "mobile_number", "email"].includes(name)) {
      setHasError((prev) => ({
        ...prev,
        [name]: value.trim() === "",
      }));
    }
  };

  const handleCategorySelectionChange = (newSelection) => {
    setFormData((prev) => ({
      ...prev,
      selectedCategories: newSelection,
    }));
    setHasError((prev) => ({
      ...prev,
      selectedCategories: false,
    }));
  };

  const handleCourseSelectionChange = (newSelection) => {
    setFormData((prev) => ({
      ...prev,
      selectedCourses: newSelection,
    }));
    setHasError((prev) => ({
      ...prev,
      selectedCourses: false,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (["first_name", "last_name", "mobile_number", "email"].includes(name)) {
      setHasError((prev) => ({
        ...prev,
        [name]: value.trim() === "",
      }));
    }
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (field === "instructor_image") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setFormData({
            ...formData,
            [field]: file,
          });
        };
        reader.readAsDataURL(file);
      } else {
        setDocumentPreviews({
          ...documentPreviews,
          [field]: URL.createObjectURL(file),
        });
        setFormData({
          ...formData,
          [field]: file,
        });
      }
    }
  };

  const removeFile = (field) => {
    if (field === "instructor_image") {
      setImagePreview(null);
      setFormData({
        ...formData,
        instructor_image: null,
      });
    } else {
      setDocumentPreviews({
        ...documentPreviews,
        [field]: null,
      });
      setFormData({
        ...formData,
        [field]: null,
      });
    }
  };

  const handleReset = () => {
    setFormData({
      first_name: "",
      last_name: "",
      mobile_number: "",
      alternate_mobile_number: "",
      address: "",
      job_title: "",
      total_experience: "",
      qualification: "",
      study_university: "",
      email: "",
      website: "",
      skills: "",
      rating: "",
      description: "",
      about_instructor1: "",
      about_instructor2: "",
      instructor_image: null,
      document1: null,
      document2: null,
      document3: null,
      document4: null,
      document5: null,
      password: "",
      is_active: 0,
      instructor_id: "",
      selectedCategories: [],
      selectedCourses: [],
    });
    setImagePreview(null);
    setDocumentPreviews({
      document1: null,
      document2: null,
      document3: null,
      document4: null,
      document5: null,
    });
    setHasError({
      first_name: false,
      last_name: false,
      mobile_number: false,
      email: false,
      selectedCategories: false,
      selectedCourses: false,
    });
    setErrorMessage("");
    setShowPassword(false);
    setAssociatedCategoriesDisplay([]);
    setAssociatedCoursesDisplay([]);
  };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const requiredFields = [
//       "first_name",
//       "last_name",
//       "mobile_number",
//       "email",
//     ];
//     const newErrors = {};
//     let hasValidationError = false;

//     requiredFields.forEach((field) => {
//       if (!formData[field].trim()) {
//         newErrors[field] = true;
//         hasValidationError = true;
//       } else {
//         newErrors[field] = false;
//       }
//     });

//     if (
//       formData.selectedCourses.length > 0 &&
//       formData.selectedCategories.length === 0
//     ) {
//       newErrors.selectedCategories = true;
//       hasValidationError = true;
//       setErrorMessage(
//         "Please select at least one category if courses are selected."
//       );
//     }

//     setHasError(newErrors);

//     if (hasValidationError) {
//       setErrorMessage(
//         "Please fill in all required fields and correct category/course selections."
//       );
//       return;
//     }

//     const documents = [
//       "document1",
//       "document2",
//       "document3",
//       "document4",
//       "document5",
//     ];
//     const hasDocument = documents.some((doc) => formData[doc]);
//     if (!hasDocument && !isEdit) {
//       setErrorMessage("Please upload at least one document before creating.");
//       return;
//     }

//     setIsSubmitting(true);
//     setErrorMessage("");

//     const submitData = new FormData();
//     if (isEdit) submitData.append("instructor_id", formData.instructor_id);
//     submitData.append("first_name", formData.first_name || "N/A");
//     submitData.append("last_name", formData.last_name || "N/A");
//     submitData.append("mobile_number", formData.mobile_number || "N/A");
//     submitData.append(
//       "alternate_mobile_number",
//       formData.alternate_mobile_number || "N/A"
//     );
//     submitData.append("address", formData.address || "N/A");
//     submitData.append("job_title", formData.job_title || "N/A");
//     submitData.append("total_experience", formData.total_experience || "N/A");
//     submitData.append("qualification", formData.qualification || "N/A");
//     submitData.append("study_university", formData.study_university || "N/A");
//     submitData.append("email", formData.email || "N/A");
//     submitData.append("website", formData.website || "N/A");
//     submitData.append("skills", formData.skills || "N/A");
//     submitData.append("rating", formData.rating || "N/A");
//     submitData.append("description", formData.description || "N/A");
//     submitData.append("about_instructor1", formData.about_instructor1 || "N/A");
//     submitData.append("about_instructor2", formData.about_instructor2 || "N/A");
//     submitData.append("is_self_registered", formData.is_self_registered || "N/A");
//     submitData.append("is_active", formData.is_active);
//     if (!isEdit || (isEdit && formData.password)) {
//       submitData.append("password", formData.password);
//     }
//     if (formData.instructor_image instanceof File)
//       submitData.append("instructor_image", formData.instructor_image);
//     else if (formData.instructor_image)
//       submitData.append("instructor_image_name", formData.instructor_image);

//     submitData.append(
//       "selected_categories",
//       JSON.stringify(formData.selectedCategories)
//     );
//     submitData.append(
//       "selected_courses",
//       JSON.stringify(formData.selectedCourses)
//     );

//     documents.forEach((doc) => {
//       if (formData[doc] instanceof File) submitData.append(doc, formData[doc]);
//       else if (formData[doc]) submitData.append(`${doc}_name`, formData[doc]);
//     });

//     let currentInstructorId = formData.instructor_id;
//     let instructorCreationSuccessful = false;

//     try {
//       const apiEndpoint = isEdit
//         ? SummaryApi.Instructorupdate
//         : SummaryApi.Instructorcreate;
//       const response = await fetch(apiEndpoint.url, {
//         method: apiEndpoint.method,
//         body: submitData,
//       });
//       const data = await response.json();

//       if (data.status) {
//         instructorCreationSuccessful = true;
        
// toast.success(
//   isEdit ? "Instructor updated successfully!" : "Instructor created successfully!",
//   { autoClose: 2000, position: "top-center" }
// );

// // If it's CREATE (not edit), redirect to the new instructor's detail page
// if (!isEdit && data.instructor_id) {
//   const encodedId = btoa(data.instructor_id);
//   // Small delay so toast can be seen
//   setTimeout(() => {
//     navigate(`/admin-panel/instructor-list?id=${encodedId}`);
//   }, 800);
//   return; // stop further execution
// }

//         try {
//           if (
//             currentInstructorId &&
//             formData.selectedCategories.length > 0 &&
//             formData.selectedCourses.length > 0
//           ) {
//             const timeslotPromises = formData.selectedCourses.map(
//               async (courseId) => {
//                 const course = allActiveCourses.find(
//                   (c) => String(c.course_id) === String(courseId)
//                 );
//                 if (
//                   course &&
//                   formData.selectedCategories.includes(
//                     String(course.category_id)
//                   )
//                 ) {
//                   return handleCreateTimeslot(
//                     currentInstructorId,
//                     String(courseId)
//                   );
//                 } else {
//                   return {
//                     status: false,
//                     message: `Skipped timeslot creation for course ID ${courseId}`,
//                   };
//                 }
//               }
//             );

//             const timeslotResults = await Promise.all(timeslotPromises);
//             const failedTimeslots = timeslotResults.filter(
//               (result) => !result.status
//             );

//             if (failedTimeslots.length > 0) {
//               console.log(
//                 "handleSubmit: Failed timeslot creations:",
//                 failedTimeslots
//               );
//             }
//           }
//         } catch (timeslotError) {
//           console.error(
//             "handleSubmit: Unexpected error during timeslot processing:",
//             timeslotError
//           );
//         }

//         if (isEdit) {
//           const latestInstructorData = await fetch(SummaryApi.InstructorByid.url, {
//             method: SummaryApi.InstructorByid.method,
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({ instructor_id: currentInstructorId }),
//           }).then((res) => res.json());

//           if (latestInstructorData.status) {
//             fetchedInstructorDataRef.current = latestInstructorData.data;
//             const response = await fetch(
//               SummaryApi.getCourseIdByInstructorId.url,
//               {
//                 method: SummaryApi.getCourseIdByInstructorId.method,
//                 headers: {
//                   "Content-Type": "application/json",
//                 },
//                 body: JSON.stringify({ instructor_id: currentInstructorId }),
//               }
//             );
//             const associatedData = await response.json();
//             if (associatedData.status) {
//               const uniqueCategories = [
//                 ...new Set(
//                   associatedData.data.map((item) => item.category_name)
//                 ),
//               ];
//               const uniqueCourses = [
//                 ...new Set(associatedData.data.map((item) => item.course_title)),
//               ];
//               setAssociatedCategoriesDisplay(uniqueCategories);
//               setAssociatedCoursesDisplay(uniqueCourses);
//             } else {
//               setAssociatedCategoriesDisplay([]);
//               setAssociatedCoursesDisplay([]);
//             }
//           } else {
//             toast.error("Failed to re-fetch instructor data after update.");
//           }

//           setTimeout(() => {
//             if (window.opener) {
//               window.close();
//             } else {
//               console.log("Update successful - you can close this tab manually");
//             }
//           }, 2000);

//         } else {
//           handleReset();
//         }
//       } else {
//         const errorMessage =
//           data.message ||
//           (isEdit
//             ? "Failed to update instructor"
//             : "Failed to create instructor");
//         setErrorMessage(errorMessage);
//         toast.error(errorMessage, {
//           autoClose: 3000,
//           position: "top-center"
//         });
//       }
//     } catch (error) {
//       console.error(
//         "handleSubmit: Critical error during main instructor API submission (network or unhandled):",
//         error
//       );
//       if (!instructorCreationSuccessful) {
//         const userFacingError = isEdit
//           ? "Error updating instructor. Please try again."
//           : "Error creating instructor. Please try again.";
//         setErrorMessage(userFacingError);
//         toast.error(userFacingError, {
//           autoClose: 3000,
//           position: "top-center"
//         });
//       }
//     } finally {
//       setIsSubmitting(false);
//     }
//   };



const handleSubmit = async (e) => {
  e.preventDefault();

  const requiredFields = [
    "first_name",
    "last_name",
    "mobile_number",
    "email",
  ];
  const newErrors = {};
  let hasValidationError = false;

  requiredFields.forEach((field) => {
    if (!formData[field].trim()) {
      newErrors[field] = true;
      hasValidationError = true;
    } else {
      newErrors[field] = false;
    }
  });

  if (
    formData.selectedCourses.length > 0 &&
    formData.selectedCategories.length === 0
  ) {
    newErrors.selectedCategories = true;
    hasValidationError = true;
    setErrorMessage(
      "Please select at least one category if courses are selected."
    );
  }

  setHasError(newErrors);

  if (hasValidationError) {
    setErrorMessage(
      "Please fill in all required fields and correct category/course selections."
    );
    return;
  }

  const documents = [
    "document1",
    "document2",
    "document3",
    "document4",
    "document5",
  ];
  const hasDocument = documents.some((doc) => formData[doc]);
  if (!hasDocument && !isEdit) {
    setErrorMessage("Please upload at least one document before creating.");
    return;
  }

  setIsSubmitting(true);
  setErrorMessage("");

  const submitData = new FormData();
  if (isEdit) submitData.append("instructor_id", formData.instructor_id);
  submitData.append("first_name", formData.first_name || "N/A");
  submitData.append("last_name", formData.last_name || "N/A");
  submitData.append("mobile_number", formData.mobile_number || "N/A");
  submitData.append(
    "alternate_mobile_number",
    formData.alternate_mobile_number || "N/A"
  );
  submitData.append("address", formData.address || "N/A");
  submitData.append("job_title", formData.job_title || "N/A");
  submitData.append("total_experience", formData.total_experience || "N/A");
  submitData.append("qualification", formData.qualification || "N/A");
  submitData.append("study_university", formData.study_university || "N/A");
  submitData.append("email", formData.email || "N/A");
  submitData.append("website", formData.website || "N/A");
  submitData.append("skills", formData.skills || "N/A");
  submitData.append("rating", formData.rating || "N/A");
  submitData.append("description", formData.description || "N/A");
  submitData.append("about_instructor1", formData.about_instructor1 || "N/A");
  submitData.append("about_instructor2", formData.about_instructor2 || "N/A");
  submitData.append("is_self_registered", formData.is_self_registered || "N/A");
  submitData.append("is_active", formData.is_active);
  if (!isEdit || (isEdit && formData.password)) {
    submitData.append("password", formData.password);
  }
  if (formData.instructor_image instanceof File)
    submitData.append("instructor_image", formData.instructor_image);
  else if (formData.instructor_image)
    submitData.append("instructor_image_name", formData.instructor_image);

  submitData.append(
    "selected_categories",
    JSON.stringify(formData.selectedCategories)
  );
  submitData.append(
    "selected_courses",
    JSON.stringify(formData.selectedCourses)
  );

  documents.forEach((doc) => {
    if (formData[doc] instanceof File) submitData.append(doc, formData[doc]);
    else if (formData[doc]) submitData.append(`${doc}_name`, formData[doc]);
  });

  try {
    const apiEndpoint = isEdit
      ? SummaryApi.Instructorupdate
      : SummaryApi.Instructorcreate;
    const response = await fetch(apiEndpoint.url, {
      method: apiEndpoint.method,
      body: submitData,
    });
    const data = await response.json();

    if (data.status) {
      toast.success(
        isEdit ? "Instructor updated successfully!" : "Instructor created successfully!",
        { autoClose: 1000, position: "top-center" }
      );

      // GET THE NEW INSTRUCTOR ID FROM RESPONSE
      const newInstructorId = data.instructor_id;
      
      // CREATE TIMESLOTS WITH THE NEW INSTRUCTOR ID (for both CREATE and EDIT)
      if (
        newInstructorId &&
        formData.selectedCategories.length > 0 &&
        formData.selectedCourses.length > 0
      ) {
        try {
          const timeslotPromises = formData.selectedCourses.map(
            async (courseId) => {
              const course = allActiveCourses.find(
                (c) => String(c.course_id) === String(courseId)
              );
              if (
                course &&
                formData.selectedCategories.includes(
                  String(course.category_id)
                )
              ) {
                return handleCreateTimeslot(
                  newInstructorId,  // Use the NEW instructor ID
                  String(courseId)
                );
              } else {
                return {
                  status: false,
                  message: `Skipped timeslot creation for course ID ${courseId}`,
                };
              }
            }
          );

          const timeslotResults = await Promise.all(timeslotPromises);
          const successfulTimeslots = timeslotResults.filter(result => result.status);
          const failedTimeslots = timeslotResults.filter(result => !result.status);

          if (successfulTimeslots.length > 0) {
            console.log(`Successfully created ${successfulTimeslots.length} timeslots`);
          }
          
          if (failedTimeslots.length > 0) {
            console.warn(`${failedTimeslots.length} timeslot creations failed or were skipped`);
          }
        } catch (timeslotError) {
          console.error(
            "handleSubmit: Unexpected error during timeslot processing:",
            timeslotError
          );
        }
      }

      // If it's CREATE (not edit), redirect to the new instructor's detail page
      if (!isEdit && newInstructorId) {
        const encodedId = btoa(newInstructorId);
        // Small delay so toast can be seen
        setTimeout(() => {
          navigate(`/admin-panel/instructor-list?id=${encodedId}`);
        }, 800);
        return; // stop further execution
      }

      if (isEdit) {
        const latestInstructorData = await fetch(SummaryApi.InstructorByid.url, {
          method: SummaryApi.InstructorByid.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instructor_id: newInstructorId }),
        }).then((res) => res.json());

        if (latestInstructorData.status) {
          fetchedInstructorDataRef.current = latestInstructorData.data;
          const response = await fetch(
            SummaryApi.getCourseIdByInstructorId.url,
            {
              method: SummaryApi.getCourseIdByInstructorId.method,
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ instructor_id: newInstructorId }),
            }
          );
          const associatedData = await response.json();
          if (associatedData.status) {
            const uniqueCategories = [
              ...new Set(
                associatedData.data.map((item) => item.category_name)
              ),
            ];
            const uniqueCourses = [
              ...new Set(associatedData.data.map((item) => item.course_title)),
            ];
            setAssociatedCategoriesDisplay(uniqueCategories);
            setAssociatedCoursesDisplay(uniqueCourses);
          } else {
            setAssociatedCategoriesDisplay([]);
            setAssociatedCoursesDisplay([]);
          }
        } else {
          toast.error("Failed to re-fetch instructor data after update.");
        }



  // --- NEW: Notify parent window (AdminInstructorDashboard) ---
  if (window.opener) {
    // Send a custom event that the dashboard is listening for
    window.opener.dispatchEvent(new Event('instructorUpdated'));
    // Optional: also trigger storage event for extra safety
    sessionStorage.setItem('instructorUpdated', Date.now().toString());
  }

  // Close the edit tab after a short delay so user sees the toast
  setTimeout(() => {
    window.close();
  }, 1200);

      } else {
        handleReset();
      }
    } else {
      const errorMessage =
        data.message ||
        (isEdit
          ? "Failed to update instructor"
          : "Failed to create instructor");
      setErrorMessage(errorMessage);
      toast.error(errorMessage, {
        autoClose: 3000,
        position: "top-center"
      });
    }
  } catch (error) {
    console.error(
      "handleSubmit: Critical error during main instructor API submission (network or unhandled):",
      error
    );
    const userFacingError = isEdit
      ? "Error updating instructor. Please try again."
      : "Error creating instructor. Please try again.";
    setErrorMessage(userFacingError);
    toast.error(userFacingError, {
      autoClose: 3000,
      position: "top-center"
    });
  } finally {
    setIsSubmitting(false);
  }
};



  return (
    <div className={`min-h-screen p-4 sm:p-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Dark Mode Toggle */}
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            {isEdit ? "Edit Instructor" : "Create New Instructor"}
          </h2>
        </div>

        <div className={`rounded-xl shadow-lg p-5 sm:p-6 mb-6 sm:mb-8 border transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-600 shadow-gray-900/50' 
            : 'bg-white border-gray-200'
        }`}>
          {errorMessage && (
            <div className={`mb-4 p-3 rounded-md transition-colors duration-300 ${
              isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
            }`}>
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Profile Section */}
            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                Profile Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaUser className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      } ${hasError.first_name ? "border-red-500" : ""}`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                        : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                    } ${hasError.last_name ? "border-red-500" : ""}`}
                    required
                  />
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaEnvelope className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      } ${hasError.email ? "border-red-500" : ""}`}
                      required
                      disabled={isEdit}
                    />
                  </div>
                </div>

                {isEdit && (
                  <div className="relative">
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Password (Leave blank to keep current password)
                    </label>
                    <div className="relative">
                      <FaLock className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`} />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter new password to update"
                        className={`block w-full border rounded-md p-1.5 pl-8 pr-10 text-sm transition-colors duration-300 ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                            : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                        }`}
                      />
                      <button
                        type="button"
                        className={`absolute right-2 top-2.5 focus:outline-none transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                        }`}
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? (
                          <FaEyeSlash className="text-sm" />
                        ) : (
                          <FaEye className="text-sm" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaPhone className={`absolute left-2 top-2.5 text-sm flip-icon transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      } ${hasError.mobile_number ? "border-red-500" : ""}`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Alternate Mobile Number
                  </label>
                  <input
                    type="text"
                    name="alternate_mobile_number"
                    value={formData.alternate_mobile_number}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                        : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                Assign Courses (for Timeslots)
              </h3>
              <p className={`text-sm mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Select one or more categories and courses to associate with this
                instructor. A timeslot will be attempted for each selected
                course upon saving.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <MultiSelectDropdown
                    label="Categories"
                    options={activeCategories.map((cat) => ({
                      value: String(cat.category_id),
                      label: cat.category_name,
                    }))}
                    selectedValues={formData.selectedCategories}
                    onSelectionChange={handleCategorySelectionChange}
                    placeholder="Select categories..."
                    hasError={hasError.selectedCategories}
                    errorMessage={
                      hasError.selectedCategories
                        ? "(Required if courses are selected)"
                        : ""
                    }
                    isLoading={isLoadingCategories}
                    loadingText="Loading categories..."
                    isDarkMode={isDarkMode}
                  />
                  {associatedCategoriesDisplay.length > 0 && (
                    <div className={`mt-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <span className="font-semibold mr-1">Currently Associated Categories:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {associatedCategoriesDisplay.map((catName, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded-full text-[12px] transition-colors duration-300 ${
                              isDarkMode 
                                ? 'bg-gray-700 text-gray-300' 
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {catName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

             <div>
  <MultiSelectDropdown
    label="Courses"
    options={availableCoursesForSelection.map((course) => ({
      value: String(course.course_id),
      label: course.course_title,
    }))}
    selectedValues={formData.selectedCourses}
    onSelectionChange={handleCourseSelectionChange}
    placeholder={
      formData.selectedCategories.length > 0
        ? isEdit && associatedCourseIds.length > 0
          ? "Select additional courses..."
          : "Select courses..."
        : "Select categories first to see available courses."
    }
    hasError={hasError.selectedCourses}
    errorMessage={
      hasError.selectedCourses
        ? "(Required if categories are selected for timeslots)"
        : ""
    }
    disabled={formData.selectedCategories.length === 0}
    isLoading={formData.selectedCategories.length > 0 && allActiveCourses.length === 0}
    loadingText="Loading courses..."
    isDarkMode={isDarkMode}
  />
  {isEdit && associatedCourseIds.length > 0 && (
    <div className={`mt-1 text-xs transition-colors duration-300 ${
      isDarkMode ? 'text-gray-400' : 'text-gray-500'
    }`}>
    </div>
  )}
  {associatedCoursesDisplay.length > 0 && (
    <div className={`mt-2 text-sm transition-colors duration-300 ${
      isDarkMode ? 'text-gray-300' : 'text-gray-700'
    }`}>
      <span className="font-semibold mr-1">Currently Associated Courses:</span>
      <div className="flex flex-wrap gap-2 mt-1">
        {associatedCoursesDisplay.map((courseName, index) => (
          <span
            key={index}
            className={`px-2 py-1 rounded-full text-[12px] transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-gray-700 text-gray-300' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            {courseName}
          </span>
        ))}
      </div>
    </div>
  )}
</div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                Professional Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Job Title
                  </label>
                  <div className="relative">
                    <FaBriefcase className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Total Experience
                  </label>
                  <input
                    type="text"
                    name="total_experience"
                    value={formData.total_experience}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                        : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Qualification
                  </label>
                  <div className="relative">
                    <FaGraduationCap className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Study University
                  </label>
                  <div className="relative">
                    <FaUniversity className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="study_university"
                      value={formData.study_university}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Website
                  </label>
                  <div className="relative">
                    <FaGlobe className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Rating
                  </label>
                  <div className="relative">
                    <FaStar className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <input
                      type="number"
                      name="rating"
                      value={formData.rating}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                      step="0.1"
                      min="0"
                      max="5"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                About Instructor
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                        : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      About Instructor 1
                    </label>
                    <textarea
                      name="about_instructor1"
                      value={formData.about_instructor1}
                      onChange={handleChange}
                      rows="3"
                      className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      About Instructor 2
                    </label>
                    <textarea
                      name="about_instructor2"
                      value={formData.about_instructor2}
                      onChange={handleChange}
                      rows="3"
                      className={`block w-full border rounded-md p-1.5 text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                          : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
   <div className="relative lg:col-span-1">
    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-300' : 'text-gray-700'
    }`}>
      Address
    </label>
    <div className="relative">
      <FaMapMarkerAlt className={`absolute left-2 top-2.5 text-sm transition-colors duration-300 ${
        isDarkMode ? 'text-blue-400' : 'text-blue-600'
      }`} />
      <textarea
        name="address"
        value={formData.address}
        onChange={handleChange}
        rows="4"
        className={`block w-full border rounded-md p-1.5 pl-8 text-sm transition-colors duration-300 resize-none ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder-gray-400'
            : 'border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600 placeholder-gray-500'
        }`}
        placeholder="Enter full address..."
      />
    </div>
  </div>

<div className="relative lg:col-span-1">
  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`}>
    Skills
  </label>
  <div className="relative">
    <div className={`rounded-md text-sm h-26 transition-colors duration-300 ${
      isDarkMode 
        ? '[&_.ql-container]:bg-gray-700 [&_.ql-editor]:text-white [&_.ql-toolbar]:bg-gray-600 [&_.ql-toolbar]:border-gray-600 [&_.ql-toolbar_.ql-stroke]:stroke-white [&_.ql-toolbar_.ql-fill]:fill-white [&_.ql-toolbar_button:hover]:bg-gray-500 [&_.ql-toolbar_.ql-active]:bg-gray-500 [&_.ql-toolbar_button:hover_.ql-stroke]:stroke-blue-300 [&_.ql-toolbar_button:hover_.ql-fill]:fill-blue-300 [&_.ql-editor.ql-blank::before]:text-gray-300' 
        : ''
    }`}>
      <ReactQuill
        theme="snow"
        value={formData.skills}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, skills: value }))
        }
        className={`rounded-md text-sm h-26 [&_.ql-container]:min-h-[96px] [&_.ql-container]:max-h-[96px] [&_.ql-editor]:min-h-[96px] [&_.ql-editor]:max-h-[96px] [&_.ql-editor]:p-2 ${
          isDarkMode ? '[&_.ql-editor]:text-white [&_.ql-editor.ql-blank::before]:text-gray-300' : ''
        }`}
        placeholder="Enter your skills..."
        modules={{
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["clean"],
          ],
        }}
      />
    </div>
  </div>
</div>
            </div>

            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                Profile Image
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Instructor Image
                  </label>
                  {imagePreview || formData.instructor_image ? (
                    <div className="relative mt-1">
                      <label className="cursor-pointer">
                        <img
                          src={
                            imagePreview ||
                            formatImageUrl(formData.instructor_image)
                          }
                          alt="Preview"
                          className="h-16 w-16 object-cover rounded-md"
                        />
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            handleFileChange(e, "instructor_image")
                          }
                          accept="image/*"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className={`flex flex-col items-center justify-center w-40 h-16 border-2 border-dashed rounded-md cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'border-gray-600 hover:border-blue-400' 
                        : 'border-gray-300 hover:border-blue-600'
                    }`}>
                      <div className="flex flex-col items-center justify-center pt-3 pb-4">
                        <FaUpload className={`w-6 h-6 mb-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <p className={`mb-1 text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Click to upload
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) =>
                          handleFileChange(e, "instructor_image")
                        }
                        accept="image/*"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className={`text-md font-semibold mb-4 border-b pb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'
              }`}>
                Documents (Optional)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  "document1",
                  "document2",
                  "document3",
                  "document4",
                  "document5",
                ].map((doc) => (
                  <div key={doc}>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {doc.charAt(0).toUpperCase() + doc.slice(1)}
                    </label>
                    {documentPreviews[doc] || formData[doc] ? (
                      <div className="relative mt-1">
                        <label className="cursor-pointer">
                          {(formData[doc] instanceof File &&
                            formData[doc].type.includes("image/")) ||
                          isImage(documentPreviews[doc] || formData[doc]) ? (
                            <img
                              src={
                                documentPreviews[doc] ||
                                formatImageUrl(formData[doc])
                              }
                              alt={`${doc} Preview`}
                              className="h-16 w-16 object-cover rounded-md"
                            />
                          ) : (
                            <div className={`h-16 w-16 flex items-center justify-center rounded-md transition-colors duration-300 ${
                              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                            }`}>
                              <FaFile className="text-2xl text-gray-400" />
                            </div>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, doc)}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.zip,.rar"
                          />
                        </label>
                      </div>
                    ) : (
                      <label className={`flex flex-col items-center justify-center w-40 h-16 border-2 border-dashed rounded-md cursor-pointer transition-colors duration-300 ${
                        isDarkMode 
                          ? 'border-gray-600 hover:border-blue-400' 
                          : 'border-gray-300 hover:border-blue-600'
                      }`}>
                        <div className="flex flex-col items-center justify-center pt-3 pb-4">
                          <FaUpload className={`w-6 h-6 mb-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`} />
                          <p className={`mb-1 text-xs transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Click to upload
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, doc)}
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.zip,.rar"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {isEdit && (
              <div className="flex items-center mt-2">
                <label
                  htmlFor="is_active_toggle"
                  className="flex items-center cursor-pointer"
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="is_active_toggle"
                      name="is_active"
                      className="sr-only"
                      checked={formData.is_active === 1}
                      onChange={handleChange}
                    />
                    <div
                      className={`block w-14 h-8 rounded-full transition-colors duration-200 ${
                        formData.is_active === 1 ? "bg-green-500" : "bg-red-500"
                      }`}
                    ></div>
                    <div
                      className={`dot absolute left-[5px] top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 shadow-md ${
                        formData.is_active === 1 ? "translate-x-6" : ""
                      }`}
                    ></div>
                  </div>
                  <div className={`ml-3 font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Instructor Active ({formData.is_active === 1 ? "Yes" : "No"})
                  </div>
                </label>
              </div>
            )}

            <div className="mt-6 flex items-center gap-4">
            <button
  type="submit"
  disabled={isSubmitting}
  className={`px-4 py-3 rounded-md text-white font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
    isDarkMode 
      ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30' 
      : 'bg-blue-900 hover:bg-blue-800 shadow-lg shadow-blue-700/30'
  }`}
>
  {isSubmitting && (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  )}
  {isSubmitting
    ? isEdit
      ? "Updating..."
      : "Creating..."
    : isEdit
    ? "Update Instructor"
    : "Create Instructor"}
</button>

              {!isEdit && (
                <button
                  type="button"
                  onClick={handleReset}
                  className={`px-4 py-3 rounded-md text-white font-medium transition-colors duration-200 ${
                    isDarkMode 
                      ? 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/30' 
                      : 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30'
                  }`}
                >
                  Reset Form
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
<style>{`
  /* Dark mode styling for ReactQuill toolbar icons */
  .ql-toolbar.ql-snow {
    ${isDarkMode ? `
      border-color: #4B5563 !important;
      background-color: #374151 !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow .ql-stroke {
    ${isDarkMode ? `
      stroke: #FFFFFF !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow .ql-fill {
    ${isDarkMode ? `
      fill: #FFFFFF !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow .ql-picker-label {
    ${isDarkMode ? `
      color: #FFFFFF !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow .ql-picker-options {
    ${isDarkMode ? `
      background-color: #374151 !important;
      border-color: #4B5563 !important;
      color: #FFFFFF !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow button:hover,
  .ql-toolbar.ql-snow button:focus,
  .ql-toolbar.ql-snow .ql-picker-label:hover,
  .ql-toolbar.ql-snow .ql-picker-item:hover {
    ${isDarkMode ? `
      background-color: #4B5563 !important;
      color: #FFFFFF !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow button:hover .ql-stroke,
  .ql-toolbar.ql-snow button:focus .ql-stroke {
    ${isDarkMode ? `
      stroke: #93C5FD !important;
    ` : ''}
  }
  
  .ql-toolbar.ql-snow button:hover .ql-fill,
  .ql-toolbar.ql-snow button:focus .ql-fill {
    ${isDarkMode ? `
      fill: #93C5FD !important;
    ` : ''}
  }
  
  .ql-container.ql-snow {
    ${isDarkMode ? `
      border-color: #4B5563 !important;
      background-color: #374151 !important;
    ` : ''}
  }

  /* Placeholder text color in dark mode */
  .ql-editor.ql-blank::before {
    ${isDarkMode ? `
      color: #D1D5DB !important;
    ` : ''}
  }
`}</style>




    </div>
  );
};

export default CreateInstructorForm;